import { selectExternalNetworkInjection } from '@/features/execute-calculation-set/mediumVoltageCalculations/selectExternalNetworkInjection';

import type { CompletedCalculationTactic } from '../../..';
import { ActivePower } from '../../../domain/ActivePower';
import { ReactivePower } from '../../../domain/ReactivePower';
import { Voltage } from '../../../domain/Voltage';
import { AttentionStatusStorage } from '../../../evaluation/AttentionStatusRepository';
import type { ForRuleProviding } from '../../../evaluation/RulesProvider';
import { AcLineSegmentEvaluationEngine } from '../../../evaluation/loadFlow/engines/mediumVoltage/acLineSegment/acLineSegmentEvaluationEngine';
import { BusbarEvaluationEngine } from '../../../evaluation/loadFlow/engines/mediumVoltage/busbar/busbarEvaluationEngine';
import { EnergyConsumerEvaluationEngine } from '../../../evaluation/loadFlow/engines/mediumVoltage/energyConsumer/energyConsumerEvaluationEngine';
import { PowerTransformerEvaluationEngine } from '../../../evaluation/loadFlow/engines/mediumVoltage/powerTransformer/powerTransformerEvaluationEngine';
import { AcLineSegmentEvaluation } from '../../../evaluation/loadFlow/gridElements/acLineSegment/acLineSegmentEvaluation';
import { BusbarEvaluation } from '../../../evaluation/loadFlow/gridElements/busbar/busbarEvaluation';
import { EnergyConsumerEvaluation } from '../../../evaluation/loadFlow/gridElements/energyConsumer/energyConsumerEvaluation';
import { PowerTransformerEvaluation } from '../../../evaluation/loadFlow/gridElements/powerTransformer/powerTransformerEvaluation';
import { filterOnlyCalculatedMvBusbarResults } from '../../../filterOnlyMvBusbarResults';
import type { MvLoadFlowSummaryEvaluatedResults } from './MvLoadFlowSummaryEvaluatedResults';

export type LoadsSummary = {
  activePowersSum: ActivePower;
  reactivePowersSum: ReactivePower;
};

export const getLoadsSummary = (
  calculationTactic: CompletedCalculationTactic
): LoadsSummary => {
  const loads =
    calculationTactic.calculationSetResult.calculationScenarios.loadFlow[0]
      .inputs.loads;

  const activePowers = ActivePower.fromWattNumber(
    loads.map(item => item.p).reduce((sum, current) => sum + current)
  );
  const reactivePowers = ReactivePower.fromVarNumber(
    loads.map(item => item.q).reduce((sum, current) => sum + current)
  );

  return {
    activePowersSum: activePowers,
    reactivePowersSum: reactivePowers
  };
};

export function getMVLoadFlowScenarioSummary(
  calculationTactic: CompletedCalculationTactic,
  rulesProvider: ForRuleProviding,
  scenariosIds: string[]
): MvLoadFlowSummaryEvaluatedResults {
  const loadFlowScenariosResults =
    calculationTactic.calculationSetResult.calculationScenarios.loadFlow.filter(
      f => scenariosIds.some(scenarioId => f.scenarioId === scenarioId)
    );

  const externalNetworkInjection = selectExternalNetworkInjection(
    calculationTactic.calculationSetResult.grid.functional
  );

  const resultsIncludingOnlyMvBusbars = filterOnlyCalculatedMvBusbarResults(
    loadFlowScenariosResults.flatMap(scenario => scenario.results),
    calculationTactic.calculationSetResult.grid.functional
  );

  const busbars = BusbarEvaluation.fromResults(
    resultsIncludingOnlyMvBusbars,
    AttentionStatusStorage.noStorage()
  ).evaluateSummary(new BusbarEvaluationEngine(rulesProvider));

  const acLineSegments = AcLineSegmentEvaluation.from(
    loadFlowScenariosResults,
    AttentionStatusStorage.noStorage()
  ).evaluateSummary(new AcLineSegmentEvaluationEngine(rulesProvider));

  const powerTransformers = PowerTransformerEvaluation.from(
    loadFlowScenariosResults,
    AttentionStatusStorage.noStorage()
  ).evaluateSummary(new PowerTransformerEvaluationEngine(rulesProvider));

  const energyConsumer = EnergyConsumerEvaluation.from(
    loadFlowScenariosResults,
    AttentionStatusStorage.noStorage()
  ).evaluateSummary(new EnergyConsumerEvaluationEngine(rulesProvider));

  const loadsSummary = getLoadsSummary(calculationTactic);

  return {
    externalNetworkInjectionVoltageSummary: {
      secondary: new Voltage(externalNetworkInjection.voltage)
    },
    busbarVoltageDiffTransformerEndPercentSummary:
      busbars.voltageDiffTransformerEndPercentResult,
    busbarVoltageSummary: busbars.voltageResult,
    attentionRequired: false,
    acLineSegmentLoadingSummary: acLineSegments.loadingResult,
    powerTransformerloadingPercentSummary: powerTransformers.loadingPercent,
    energyConsumerVoltageDiffTransformerEndPercentSummary:
      energyConsumer.voltageDiffTransformerEndPercentResult,
    energyConsumerVoltagesSummary: energyConsumer.voltageResult,
    activePowerSummary: loadsSummary.activePowersSum,
    reactivePowerSummary: loadsSummary.reactivePowersSum
  };
}
